import React, { useEffect, useState } from 'react'
import cls from 'classnames'
import PropTypes from 'prop-types'
import styled from 'styled-components'

const SIDEBAR_TYPE = {
  ICON: 'icon',
  STATIC: 'static',
  DRAWER: 'drawer',
}

const Sidebar = ({ className, type = SIDEBAR_TYPE.ICON, options }) => {
  const [stateOptions, setStateOptions] = useState(options)

  useEffect(() => {
    setStateOptions(options)
  }, [options])

  const handleItemClick = (item) => {
    if (!item.disabled) {
      setStateOptions((prev) =>
        prev.map((prevItem) => ({ ...prevItem, selected: prevItem.id === item.id }))
      )
      item.onClick()
    }
  }

  return (
    <div className={className}>
      {type === SIDEBAR_TYPE.ICON
        ? stateOptions.map((item) => (
            <div
              id={item.id}
              key={item.id}
              onClick={() => handleItemClick(item)}
              className="sidebar-item-container"
              data-pendo-feature={item.dataPendoFeature}
            >
              <div
                className={cls('item-wrapper', {
                  disabled: item.disabled,
                  selected: item.selected,
                  default: !item.disabled && !item.selected,
                })}
                role="button"
              >
                <item.Icon />
                <p style={{ margin: 0 }}>{item.label}</p>
              </div>
              {item.Badge ? <item.Badge /> : null}
            </div>
          ))
        : null}
      <div className="page-tour-container" />
    </div>
  )
}

const StyledSidebar = styled(Sidebar)`
  width: 60px;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-right: 1px solid var(--color-border);
  .sidebar-item-container {
    width: 59px;
    height: 60px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    .item-wrapper {
      width: 53px;
      height: 48px;
      /* content-box so the selected-state border (1px in dark, none in light —
         see --sidebar-item-selected-border) draws OUTSIDE the 53px content box
         instead of eating 2px off the inside. With border-box the dark border
         shrank the usable width to 51px, wrapping longer labels (e.g. the bold
         selected "SOC Agent") to a second line in dark only. The 1px outline
         still fits inside the 59px container, so nothing clips. */
      box-sizing: content-box;
      border-radius: 10px;
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 2px;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      user-select: none;

      p {
        /* Poppins is wider than the previous font: keep two-word labels
           ("SOC Agent") on one line, and centre them so a label wider than the
           53px box stays symmetrical around its icon instead of running off
           the right. */
        white-space: nowrap;
        width: 100%;
        text-align: center;
      }

      &.default {
        background: none;
        color: var(--sub-text-color);
        svg,
        svg > path {
          fill: var(--sub-text-color);
        }
        p {
          color: var(--sub-text-color);
          font-weight: 400;
          font-size: 10px;
        }
        &:hover {
          background-color: var(--ds-bg-pale-blue);
          color: var(--sidebar-item-hover-icon);
          svg,
          svg > path {
            fill: var(--sidebar-item-hover-icon);
          }
          p {
            color: var(--sidebar-item-hover-text);
            font-weight: 600;
            font-size: 10px;
          }
        }
      }
      &.selected {
        background: var(--sidebar-item-selected-background);
        border: var(--sidebar-item-selected-border);
        color: var(--primary-color);
        svg,
        svg > path {
          fill: var(--primary-color);
        }
        p {
          color: var(--primary-color);
          font-weight: 600;
          font-size: 10px;
        }
      }
      &.disabled {
        background: none;
        cursor: default;
        color: var(--ds-gray-5);
        svg,
        svg > path {
          fill: var(--ds-gray-5);
        }
        p {
          color: var(--ds-gray-5);
          font-weight: 400;
          font-size: 10px;
        }
      }
    }
  }
  .page-tour-container {
    width: 136px;
    height: 32px;
    position: absolute;
    bottom: 24px;
    left: 5px;
    z-index: var(--z-index-modal);
  }
`

StyledSidebar.SIDEBAR_TYPE = SIDEBAR_TYPE

StyledSidebar.propTypes = {
  type: PropTypes.oneOf(Object.values(SIDEBAR_TYPE)),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      Icon: PropTypes.elementType.isRequired,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      selected: PropTypes.bool,
      Badge: PropTypes.elementType,
    })
  ),
}

StyledSidebar.defaultProps = {
  type: SIDEBAR_TYPE.ICON,
  options: [],
}

export default StyledSidebar
